import { useDispatch, useSelector } from 'react-redux';
import {
  alarmIdSelector, updateAlarm, updateAlarmAsset,
} from 'features/details';
import { useWebSocketContext } from 'lib';
import { useEffect } from 'react';
import {
  addCallId,
  closeHelperCall,
  callIdsSelector,
  removeCallEvent,
  removeParticipant,
  upsertCallEvent,
  refreshLogEvent,
  upsertLogEvent,
  upsertParticipant,
} from 'features/details/chat';
import { AlarmCallType, AlarmLogEvent } from '@x-guard/xgac-types/xgac';
import { PopulatedAlarm, PopulatedAlarmProfessionalResponder } from 'types';
import { addHelper, updateHelper } from 'features/details/helpers';
import { createResponder, updateResponder } from 'features/details/response/professional';

export const useAlarmDetailsSubscription = () => {

  const alarmId = useSelector(alarmIdSelector);
  const callIds = useSelector(callIdsSelector);
  const { ws, retryIndex } = useWebSocketContext();
  const dispatch = useDispatch();

  useEffect(() => {

    if (!alarmId || !ws) return undefined;

    const revealLogEvent = (document: AlarmLogEvent) => {

      dispatch(upsertLogEvent(document));

      const { createdBy } = document;

      if (createdBy?._ref !== 'User' || createdBy.name) return;

      dispatch(refreshLogEvent({ alarmId: document.alarm._id, eventId: document._id }));

    };

    return ws.subscribe('Alarm', {
      events: ['create', 'update', 'remove'],
      id: [alarmId],
      handler: {
        Asset: {
          update: ({ document }) => dispatch(updateAlarmAsset(document)),
        },
        AlarmHelper: {
          update: ({ document }) => dispatch(updateHelper(document)),
          create: ({ document }) => dispatch(addHelper(document)),
        },
        AlarmLogEvent: {
          create: ({ document }) => revealLogEvent(document),
          update: ({ document }) => revealLogEvent(document),
        },
        AlarmCall: {
          create: ({ document }) => {

            if (document.type === AlarmCallType.HelperCall) dispatch(addCallId(document._id));

          },
          update: ({ document }) => {

            if (document.type === AlarmCallType.HelperCall && document.endedAt) {

              dispatch(closeHelperCall(document._id));

            }

          },
        },
        Alarm: {
          update: ({ document }) => dispatch(updateAlarm(document as PopulatedAlarm)),
        },
        AlarmProfessionalResponder: {
          update: ({ document }) => dispatch(updateResponder(document)),
          create: ({ document }) => dispatch(createResponder(document as PopulatedAlarmProfessionalResponder)),
        },
      },
    });

  }, [alarmId, ws, dispatch, retryIndex]);

  useEffect(() => {

    if (!ws || callIds.length === 0) return undefined;

    const stops = callIds.map((callId) => ws.subscribe('AlarmCall', {
      events: ['create', 'update', 'remove'],
      id: [callId],
      handler: {
        AlarmCallEvent: {
          create: ({ document }) => dispatch(upsertCallEvent(document)),
          update: ({ document }) => dispatch(upsertCallEvent(document)),
          remove: ({ document }) => dispatch(removeCallEvent(document._id)),
        },
        AlarmCallParticipant: {
          create: ({ document }) => dispatch(upsertParticipant(document)),
          update: ({ document }) => dispatch(upsertParticipant(document)),
          remove: ({ document }) => dispatch(removeParticipant(document._id)),
        },
      },
    }));

    return () => {

      stops.forEach((stop) => stop());

    };

  }, [callIds, dispatch, retryIndex, ws]);

};
